import { Minus, Plus } from "lucide-react";
import * as React from "react";

import { RenderIf } from "../../common/RenderIf";
import { cn } from "../../lib/utils";

interface TreeNodeProps {
	children?: React.ReactNode;
	className?: string;
	title: React.ReactNode;
	initialOpen?: boolean;
	isExpandable?: boolean;
	onClick?: () => void;
}

const TreeNode = ({
	children = null,
	className,
	title,
	initialOpen = false,
	isExpandable = true,
	onClick,
}: TreeNodeProps) => {
	const [isOpen, setIsOpen] = React.useState(initialOpen ?? false);

	const treeChildren = React.useMemo(() => {
		if (!children) return null;
		return React.Children.map(children, (child) => {
			if (!React.isValidElement(child)) return child;
			return React.cloneElement(child, {
				className: cn("border-l-2 border-gray-200 pl-4 py-2", child.props.className),
			} as React.HTMLAttributes<HTMLElement>);
		});
	}, [children]);

	const onExpansionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setIsOpen((_isOpen) => !_isOpen);
	};

	return (
		<div className={cn("flex flex-col w-full", className)}>
			<div
				role="button"
				onClick={onClick}
				className={cn("flex justify-between w-full items-center", {
					"mb-2": Boolean(treeChildren),
				})}
			>
				{title}
				<RenderIf condition={isExpandable && Boolean(treeChildren)}>
					<button className="p-2" onClick={onExpansionClick}>
						<RenderIf condition={isOpen}>
							<Minus size={16} />
						</RenderIf>
						<RenderIf condition={!isOpen}>
							<Plus size={16} />
						</RenderIf>
					</button>
				</RenderIf>
			</div>
			<RenderIf condition={isOpen}>
				<div className="pl-4">{treeChildren}</div>
			</RenderIf>
		</div>
	);
};

TreeNode.displayName = "TreeNode";

export { TreeNode };
